import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";

import { baseDrawControlModes } from "fond/draw/helper";
import { onAfterDragAerial } from "fond/draw/modes/planner/aerial/onAfterDragAerial";
import { onTrashAerial } from "fond/draw/modes/planner/aerial/onTrashAerial";
import { onAfterDragUnderground } from "fond/draw/modes/planner/underground/onAfterDragUnderground";
import { onCreateUndergroundPath } from "fond/draw/modes/planner/underground/onCreateUndergroundPath";
import { onCreateUndergroundPoint } from "fond/draw/modes/planner/underground/onCreateUndergroundPoint";
import { onTrashUnderground } from "fond/draw/modes/planner/underground/onTrashUnderground";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import { makeValidateAerialPole, makeValidateLeadIn, makeValidateUgPath, makeValidateUgPoint } from "fond/draw/validation";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { getCurrentProjectData } from "fond/project";
import { featureCollection } from "fond/turf";
import { EditingLayer, LayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import { compositeDrawLineString, compositeDrawPoint } from "./modes";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLine" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

// Create custom validation functions for ExtendedDesign layers
const validateLeadIn = makeValidateLeadIn({
  demandLayerId: LayerIds.inAddress,
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
});

const validateUgPoint = makeValidateUgPoint({
  ugPathLayerId: LayerIds.inStreet,
  leadinsLayerId: LayerIds.inLeadIns,
});

const validateUgPath = makeValidateUgPath({
  ugPointsLayerId: LayerIds.inStreetPoints,
  aerialPoleLayerId: LayerIds.inPole,
});

const validateAerialPole = makeValidateAerialPole({
  spanLayerId: LayerIds.inSpan,
});

/**
 * This component is responsible for managing the drawing of Lead Ins and Underground layers
 * for Extended Design data prep modifications.
 */
const ModificationsDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      topologicalEditing: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      modes: {
        ...baseDrawControlModes,
        draw_point: compositeDrawPoint,
        draw_line_string: compositeDrawLineString,
      },
      editOptions: {
        allLayers: [
          {
            ID: LayerIds.inAddress,
            GeometryType: "Point",
            getFeatureProperties: () => ({ layerId: LayerIds.inAddress }),
          },
          {
            ID: LayerIds.inLeadIns,
            GeometryType: "LineString",
            validate: validateLeadIn,
            options: {
              // Leadins are always a single segment between a demand point and the network.
              midPoints: false,
              maxVertices: 2,
            },
            getFeatureProperties: () => ({ layerId: LayerIds.inLeadIns }),
          },
          {
            ID: LayerIds.inStreetPoints,
            GeometryType: "Point",
            validate: validateUgPoint,
            onCreate: onCreateUndergroundPoint,
            onAfterDrag: onAfterDragUnderground,
            onTrash: onTrashUnderground,
          },
          {
            ID: LayerIds.inStreet,
            GeometryType: "LineString",
            validate: validateUgPath,
            onCreate: onCreateUndergroundPath,
            onAfterDrag: onAfterDragUnderground,
            onTrash: onTrashUnderground,
            // No getFeatureProperties here — onCreateUndergroundPath sets layerId
            // on each feature directly. Using getFeatureProperties would cause
            // handleOnCreate to overwrite ALL features (including auto-created
            // points) with { layerId: inStreet }, breaking point styling.
          },
          {
            ID: LayerIds.inSpan,
            GeometryType: "LineString",
            options: { midPoints: false },
            // The span layer enforced topological editing with the pole layer.
            compulsoryTopologicalLayerIds: [LayerIds.inPole, LayerIds.inSpan],
            // The span layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
            onAfterDrag: onAfterDragAerial,
            onTrash: onTrashAerial,
          },
          {
            ID: LayerIds.inPole,
            GeometryType: "Point",
            validate: validateAerialPole,
            // The pole layer enforced topological editing with the span layer.
            compulsoryTopologicalLayerIds: [LayerIds.inPole, LayerIds.inSpan],
            // The pole layer has custom onAfterDrag and onTrash handlers to manage pole merging, span splitting, and snapped status.
            onAfterDrag: onAfterDragAerial,
            onTrash: onTrashAerial,
          },
        ] as EditingLayer[],
        editingLayerId: LayerIds.inLeadIns,
      },
    }),
    []
  );

  // Get features from Redux (same source as MapContent) to ensure IDs match what's on the map
  const initialFeatures = useMemo(() => {
    if (!projectData?.layers) return undefined;

    const allFeatures: Feature[] = [];
    const layerKeys = [LayerIds.inAddress, LayerIds.inLeadIns, LayerIds.inStreetPoints, LayerIds.inStreet, LayerIds.inSpan, LayerIds.inPole];

    layerKeys.forEach((layerKey) => {
      const layerData = projectData.layers[layerKey];
      if (layerData?.features) {
        allFeatures.push(...layerData.features);
      }
    });

    return allFeatures.length > 0 ? featureCollection(allFeatures) : undefined;
  }, [projectData]);

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={defaultOptions} config={allToolConfig} initialFeatures={initialFeatures}>
      <MultiLayerEditPanelContent />
    </MultiLayerEditLegacyProvider>
  );
};

export default ModificationsDraw;
